import { useCallback, useState } from 'react';
import isMatch from 'lodash/isMatch';
import type { VcsApi } from '@env0/vcs-service/api';
import type { OAuthResult } from 'components/organizations/vcs/vcs-connection-modal/vcs-connection-modal-form-oauth-authorization-button';
import { useCreateVcsConnection, useGetVcsConnections, useUpdateVcsConnection } from 'stores/rq/vcs-connections';

export const useVcsConnectionCreator = (
  vcsType: VcsApi.VcsType,
  connectionType: VcsApi.ConnectionType,
  onSuccess?: () => void
) => {
  const { mutateAsync: createVcsConnection } = useCreateVcsConnection();
  const { mutateAsync: updateVcsConnection } = useUpdateVcsConnection();
  const { data: existingConnections } = useGetVcsConnections();
  const [isCreatingConnection, setIsCreatingConnection] = useState<boolean>(false);

  const handleOAuthSuccess = useCallback(
    async (result: OAuthResult): Promise<void> => {
      try {
        const existingConnection = existingConnections?.find(connection =>
          isMatch(connection, {
            accessScope: result.accessScope,
            type: vcsType,
            connectionType: connectionType
          })
        );

        if (existingConnection) {
          await updateVcsConnection({
            id: existingConnection.id!,
            data: {
              authorizationId: result.authorizationId
            }
          });
        } else {
          await createVcsConnection({
            type: vcsType,
            connectionType: connectionType,
            authorizationId: result.authorizationId
          });
        }

        onSuccess?.();
      } catch (error) {
        console.error('Failed to create/update VCS connection:', error);
      } finally {
        setIsCreatingConnection(false);
      }
    },
    [createVcsConnection, updateVcsConnection, existingConnections, vcsType, onSuccess, connectionType]
  );

  const handleOAuthError = useCallback((error: string) => {
    console.error('OAuth error:', error);
    setIsCreatingConnection(false);
  }, []);

  return {
    handleOAuthSuccess,
    handleOAuthError,
    isCreatingConnection,
    setIsCreatingConnection
  };
};
